import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import { isInsideSalesAdmin } from '../../../utils/postHog';
import CoachSelect from '../../components/Leads/components/CoachSelect';
import CoachesListContext from '../../context/CoachesListContext';
import LoggedInUserContext from '../../../context/LoggedInUserContext';
import ConfirmationModal from '../../../components/v2/ConfirmationModal/ConfirmationModal';
import CoachHistory, { HistoryEventType } from '../../Model/CoachHistory';
import useToast from '../../hooks/useToast';
import useComponentMounted from '../../../hooks/useComponentMounted';
import {
  StyledHeader,
  Title,
  NoCoachText,
  StyledTabs,
  StyledButton,
  OptionContainer,
} from './styles';
import texts from './texts.json';

const CoachConfigurations = ({
  defaultTab,
  tabsConfig,
  role,
  isISConfigView,
}) => {
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const [selectedCoach, setSelectedCoach] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [note, setNote] = useState('');

  const {
    coachesCollection,
  } = useContext(CoachesListContext);
  const { userId, userDoc } = useContext(LoggedInUserContext);

  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const tabContent = useMemo(() => {
    const { component: TabContent, props } = tabsConfig[selectedTab];

    if (!selectedCoach.id) {
      return (
        <NoCoachText>{texts.noCoachSelected}</NoCoachText>
      );
    }

    return (
      <TabContent
        coach={selectedCoach}
        {...props}
      />
    );
  }, [
    tabsConfig,
    selectedTab,
    selectedCoach,
  ]);

  const selectedCoachData = useMemo(() => (
    coachesCollection.docs.find(({ id }) => id === selectedCoach.id)
  ), [coachesCollection.docs, selectedCoach.id]);

  const handleDisableIS = useCallback(async () => {
    try {
      if (selectedCoach.id && selectedCoachData) {
        await selectedCoachData.updateISEnrollment(false);
        await CoachHistory.addCoachHistoryItem({
          type: HistoryEventType.IS_FLAG_UPDATED,
          coachId: selectedCoach.id,
          actionedBy: userId,
          actionedByUserName: userDoc?.name,
          metadata: {
            previousValue: true,
            newValue: false,
            ...(note && { note }),
          },
        });
      }
    } catch (error) {
      showToast(format(texts.disableISError, { error: error.message }), { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setShowConfirmationModal(false);
        setNote('');
      }
    }
  }, [selectedCoach.id, selectedCoachData, userId, userDoc, note, showToast, isComponentMountedRef]);

  return (
    <>
      <StyledHeader>
        <Title>{texts.title}</Title>
        <OptionContainer>
          <CoachSelect role={role} onChange={(coach) => setSelectedCoach(coach || {})} />
          {isInsideSalesAdmin() && isISConfigView && !!selectedCoachData?.isEnrolledInISProgram
            && (
              <StyledButton onClick={() => setShowConfirmationModal(true)}>
                {texts.disableIS}
              </StyledButton>
            )}

        </OptionContainer>
      </StyledHeader>
      <StyledTabs
        tabsConfig={tabsConfig}
        selectedTab={selectedTab}
        onSelectTab={setSelectedTab}
      />
      {tabContent}
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={() => { setShowConfirmationModal(false); setNote(''); }}
        onConfirm={handleDisableIS}
        title={texts.modalTitle}
        description={texts.modalContent}
        showAdditionalInput
        onInputChange={setNote}
      />
    </>
  );
};

CoachConfigurations.propTypes = {
  defaultTab: PropTypes.string.isRequired,
  tabsConfig: PropTypes.object.isRequired,
  role: PropTypes.string,
  isISConfigView: PropTypes.bool,
};

CoachConfigurations.defaultProps = {
  role: '',
  isISConfigView: false,
};

export default CoachConfigurations;
